import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProfessors, getUniversities, getDepartments, getResearchAreas, Professor, Pagination } from '../../services/professorService';
import FilterPanel from './FilterPanel';
import ProfessorCard from './ProfessorCard';
import './ProfessorList.css';

// Define University interface
interface University {
  id: number;
  name: string;
}

// Define Department interface
interface Department {
  id: number;
  name: string;
  university_id: number;
}

// Define ResearchArea interface
interface ResearchArea {
  id: number;
  name: string;
}

const ProfessorList: React.FC = () => {
  // State for professors data
  const [professors, setProfessors] = useState<Professor[]>([]);
  const [filteredProfessors, setFilteredProfessors] = useState<Professor[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [researchAreas, setResearchAreas] = useState<ResearchArea[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // State for filters
  const [filters, setFilters] = useState({
    searchQuery: '',
    university: '',
    department: '',
    researchArea: ''
  });

  // Fetch metadata (universities, departments, research areas) once
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        // Only fetch metadata if not already loaded
        if (universities.length === 0 || departments.length === 0 || researchAreas.length === 0) {
          console.log('Fetching metadata (universities, departments, research areas)');
          const [universitiesData, departmentsData, researchAreasData] = await Promise.all([
            getUniversities(),
            getDepartments(),
            getResearchAreas()
          ]);
          
          setUniversities(universitiesData);
          setDepartments(departmentsData);
          setResearchAreas(researchAreasData);
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
        // Don't set error state for metadata - it's not critical for the main functionality
        // But log it for debugging purposes
      }
    };
    
    fetchMetadata();
  }, []); // Empty dependency array means this runs once on component mount
  
  // Fetch professors data when page or filters change
  useEffect(() => {
    const fetchProfessors = async () => {
      try {
        setLoading(true);
        
        // Fetch professors data with filters
        const professorsResponse = await getProfessors(currentPage, 20, filters);
        
        // Log the response for debugging
        console.log('Professors response:', professorsResponse);
        
        // Set professors data
        setProfessors(professorsResponse.professors);
        setFilteredProfessors(professorsResponse.professors);
        setPagination(professorsResponse.pagination);
        
        // Log the number of professors
        console.log(`Set ${professorsResponse.professors.length} professors, total: ${professorsResponse.pagination.total}`);
      } catch (err) {
        console.error('Error fetching professors:', err);
        if (err instanceof Error) {
          setError(`Failed to fetch professors: ${err.message}`);
        } else {
          setError('Failed to fetch professors. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfessors();
  }, [currentPage, filters]);
  
  // No need for client-side filtering anymore - filters are applied server-side
  // The professors state already contains the filtered results from the API
  
  // Handle filter changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset to page 1 when filters change
    setCurrentPage(1);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      university: '',
      department: '',
      researchArea: ''
    });
  };

  // Log current state before rendering
  console.log('Rendering ProfessorList with state:', {
    professorsCount: professors.length,
    filteredProfessorsCount: filteredProfessors.length,
    pagination,
    currentPage,
    loading,
    error
  });
  
  return (
    <div className="professor-list-page">
      <div className="container py-5">
        <h1 className="mb-4">Browse Professors</h1>
        
        {/* Search and Filters */}
        <FilterPanel
          universities={universities}
          departments={departments}
          researchAreas={researchAreas}
          professors={professors}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />
        
        {/* Results */}
        <div className="row">
          <div className="col">
            {loading ? (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading professors...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : filteredProfessors.length === 0 ? (
              <div className="text-center my-5">
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <i className="bi bi-search"></i>
                  </div>
                  <h3>No professors found</h3>
                  <p>Try adjusting your search criteria</p>
                  <button className="btn btn-primary" onClick={clearFilters}>Reset Filters</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-muted mb-4">
                  {pagination ? 
                    `Showing ${filteredProfessors.length} of ${pagination.total} professors (Page ${pagination.page} of ${pagination.pages})` : 
                    `Showing ${filteredProfessors.length} professors`}
                </p>
                
                <div className="row">
                  {filteredProfessors.map(professor => (
                    <div key={professor.id} className="col-md-6 col-lg-4 mb-4">
                      <ProfessorCard professor={professor} />
                    </div>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {pagination && pagination.pages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <nav aria-label="Professor pagination">
                      <ul className="pagination">
                        <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={!pagination.has_prev}
                          >
                            Previous
                          </button>
                        </li>
                        
                        {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current page
                            return page === 1 || 
                                   page === pagination.pages || 
                                   Math.abs(page - currentPage) <= 2;
                          })
                          .map((page, index, array) => {
                            // Add ellipsis where needed
                            if (index > 0 && array[index - 1] !== page - 1) {
                              return [
                                <li key={`ellipsis-${page}`} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>,
                                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ];
                            }
                            return (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          })
                        }
                        
                        <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={!pagination.has_next}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessorList;
